import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AdminSidebar } from './AdminSidebar';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCommandMenu } from '@/components/admin/search/CommandMenuContext';
import { Search, Globe, LogOut, User } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AdminLayoutProps {
    children?: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
    const { direction, language, setLanguage } = useLanguage();
    const { setOpen } = useCommandMenu();
    const navigate = useNavigate();

    const toggleLanguage = () => {
        setLanguage(language === 'ar' ? 'en' : 'ar');
    };

    const handleLogout = async () => {
        try {
            await supabase.auth.signOut();
            localStorage.removeItem('mock_session');
            localStorage.removeItem('mock_auth_user');
            navigate('/admin/login');
        } catch (error) {
            console.error('Logout failed:', error);
            navigate('/admin/login');
        }
    };

    return (
        <div className="min-h-screen bg-[#f1f2f3] font-sans" dir={direction}>
            {/* Top Navigation (Shopify Style) */}
            <header className="bg-[#1a1a1a] text-white h-14 flex items-center justify-between px-4 sticky top-0 z-50">
                <div className="flex items-center gap-4">
                    <div className="font-bold text-lg tracking-tight flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#008060] rounded flex items-center justify-center">
                            <span className="font-bold text-white">S</span>
                        </div>
                        <span className="hidden sm:block">Shopify</span>
                    </div>
                    <div
                        onClick={() => setOpen(true)}
                        className="bg-[#303030] rounded-md flex items-center px-3 py-1.5 w-64 border border-[#4a4a4a] hover:border-gray-400 cursor-pointer transition-colors hidden md:flex"
                    >
                        <Search size={16} className="text-gray-400" />
                        <span className="text-sm ml-2 text-gray-400">
                            {language === 'ar' ? 'بحث (Ctrl+K)' : 'Search (Ctrl+K)'}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <button
                        onClick={toggleLanguage}
                        className="flex items-center gap-1 text-sm hover:bg-[#303030] px-2 py-1 rounded text-gray-300 hover:text-white transition-colors"
                    >
                        <Globe size={16} />
                        <span>{language === 'en' ? 'English' : 'العربية'}</span>
                    </button>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <div className="flex items-center gap-2 cursor-pointer hover:bg-[#303030] p-1 rounded transition-colors">
                                <div className="w-8 h-8 bg-[#008060] rounded-full flex items-center justify-center text-sm font-medium">
                                    VO
                                </div>
                                <div className="text-sm hidden sm:block">
                                    <div className="font-medium">Voo Store</div>
                                </div>
                            </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 bg-[#1a1a1a] text-white border-[#303030]">
                            <DropdownMenuLabel>حسابي</DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-[#303030]" />
                            <DropdownMenuItem className="focus:bg-[#303030] focus:text-white cursor-pointer">
                                <User className="mr-2 h-4 w-4 ml-2" />
                                <span>الملف الشخصي</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem className="focus:bg-[#303030] focus:text-white cursor-pointer text-red-400 focus:text-red-400" onClick={handleLogout}>
                                <LogOut className="mr-2 h-4 w-4 ml-2" />
                                <span>تسجيل الخروج</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <div className="flex min-h-[calc(100vh-3.5rem)]">
                <AdminSidebar />

                <main className="flex-1 p-4 md:p-8 overflow-x-hidden max-w-7xl mx-auto w-full">
                    {children || <Outlet />}
                </main>
            </div>
        </div>
    );
}
